import { TagToneType } from './channel-insights-constants';

// The blended Model Highlights container: four headline tiles above six highlight cards. Both
// follow the date picker and always read actualized results.

// What the selected range is compared against.
export const ComparisonMode = {
  // Up to MAX_SAME_RANGE_WEEKS: the whole range vs the same weeks a year earlier.
  SAME_WEEKS: 'same_weeks',
  // Longer than that: the latest 52 weeks vs the same 52 weeks a year earlier...
  LATEST_YEAR: 'latest_year',
  // ...or, once the range reaches back two or more whole years, vs the earliest of those same
  // weeks still inside it, so both sides cover the same calendar months.
  FIRST_VS_LATEST: 'first_vs_latest',
  // Fewer than MIN_COMPARE_WEEKS, or the year-earlier weeks fall outside the data.
  NONE: 'none'
} as const;
export type ComparisonModeType = (typeof ComparisonMode)[keyof typeof ComparisonMode];

export interface HighlightChip {
  label: string;
  tone: TagToneType;
}

export interface HighlightCard {
  id: string;
  // Feature name for channel cards (opens the drawer on click); undefined for portfolio cards.
  featureName: string | undefined;
  title: string;
  // Short context at the top right: a window, a date, or average weekly spend.
  context: string;
  // The features the context lists by name (the went-dark card's channels), so the ones with an
  // incrementality test can carry a beaker there.
  contextFeatures?: string[];
  headline: string;
  // At most one insight chip and one suggestion per card.
  chip: HighlightChip | undefined;
  suggestion: string | undefined;
}

export interface ModelHighlightsResult {
  portfolio: HighlightCard[];
  channels: HighlightCard[];
  comparison: ComparisonModeType;
  // Fewer than MIN_RANGE_WEEKS weeks are selected, so no cards are built.
  tooShort: boolean;
}

export const HeadlineTileKind = {
  RETURN: 'return',
  RESULTS: 'results',
  SPEND: 'spend',
  ACCURACY: 'accuracy'
} as const;
export type HeadlineTileKindType = (typeof HeadlineTileKind)[keyof typeof HeadlineTileKind];

// One plotted point in a tile's chart. The tile's sentence reads the same points the chart draws,
// so text and line can never disagree. `predicted` is set only on the accuracy tile.
export interface TilePoint {
  key: string;
  label: string;
  value: number;
  predicted?: number;
  // The same weeks a year earlier, drawn as the dashed line on hover. Set on the return, results
  // and spend tiles for ranges up to a year whose year-earlier weeks are all in the data; the
  // sentences and chips never read it.
  yearEarlier?: number;
  yearEarlierLabel?: string;
}

// Whether a tile's chart plots weeks or months, which also names the tooltip's two panels.
export const TilePeriod = {
  WEEK: 'week',
  MONTH: 'month'
} as const;
export type TilePeriodType = (typeof TilePeriod)[keyof typeof TilePeriod];

export interface HeadlineTile {
  kind: HeadlineTileKindType;
  period: TilePeriodType;
  title: string;
  context: string;
  value: string;
  unit: string;
  points: TilePoint[];
  headline: string;
  chip: HighlightChip | undefined;
}

// ---- Card rules kept from the fixed-window Model Highlights (channel-insights-constants.ts) ----
// The next dollar earns at least this share of the dollars it averaged: room to scale.
export const ROOM_TO_SCALE_MIN_RATIO = 0.8;
// The next dollar earns at most this share of the dollars it averaged: diminishing returns.
export const DIMINISHING_MAX_RATIO = 0.5;
// A channel's return per dollar vs the comparison weeks.
export const RETURN_TREND_MIN_CHANGE = 0.25;
// A channel's weekly spend vs the comparison weeks; a trend leads the card only alongside this.
export const SPEND_SHIFT_MIN_CHANGE = 0.5;
// Share of results vs share of spend.
export const ABOVE_WEIGHT_MIN_RATIO = 2;
export const BELOW_WEIGHT_MAX_RATIO = 0.5;
// A week counts as a spike at this multiple of the median week over the 52 weeks ending at the range end.
export const SPIKE_MIN_RATIO = 1.5;
// A month counts as busy at this multiple of an average month across the full history; the
// biggest busy month is the seasonal peak.
export const SEASONAL_PEAK_MIN_INDEX = 1.3;
// A year reads as highly seasonal once its peak month runs at least this multiple of its quietest.
export const HIGHLY_SEASONAL_MIN_RATIO = 2;
// Revenue KPIs: $1 back per $1 spent.
export const BREAK_EVEN_RETURN = 1;
// A channel needs at least this share of paid spend in the range to get a card.
export const MIN_CHANNEL_SPEND_SHARE = 0.005;
// A week's spend only counts when it clears both guards: at least a dollar, and at least this
// share of the channel's typical week over the 52 weeks ending at the range end.
export const MIN_WEEKLY_SPEND = 1;
export const MIN_SHARE_OF_TYPICAL = 0.01;

// ---- Date-range rules ----
// Fewer weeks than this and the container shows no cards.
export const MIN_RANGE_WEEKS = 4;
// Fewer weeks than this and nothing is compared with a year earlier.
export const MIN_COMPARE_WEEKS = 8;
// Up to this many weekly rows, the whole range is compared. 53 rather than 52 because the
// trailing-12-month preset spans 53 weekly rows.
export const MAX_SAME_RANGE_WEEKS = 53;
export const WEEKS_PER_YEAR = 52;
export const DAYS_PER_YEAR_SHIFT = 364;
// Suggestions always read the latest 13 model weeks, and appear only when the range ends within
// this many weeks of the latest model week.
export const SUGGESTION_WEEKS = 13;
export const SUGGESTION_END_WITHIN_WEEKS = 4;
// A channel comparison is skipped while it is still launching: the smaller side must be at least
// this share of the bigger side, and the year-earlier side must have real spend in at least this
// share of its weeks.
export const LAUNCH_MIN_SIDE_RATIO = 0.25;
export const LAUNCH_MIN_LIVE_SHARE = 0.5;
// Portfolio return change vs the comparison weeks: under STEADY reads "Holding steady", under
// SLIGHT reads "Slightly up / down", otherwise "Return rising / falling".
export const PORTFOLIO_STEADY_MAX_CHANGE = 0.05;
export const PORTFOLIO_SLIGHT_MAX_CHANGE = 0.1;
// Went dark: real spend in the LEAD weeks before the range's last TAIL weeks, ~nothing in the TAIL.
export const DARK_TAIL_WEEKS = 4;
export const DARK_LEAD_WEEKS = 13;
// A channel must have carried at least this share of paid spend in the lead weeks to take the slot.
export const DARK_MIN_SPEND_SHARE = 0.02;
export const MAX_DARK_FEATURES_PER_CARD = 3;
// A busy month counts as "in these dates" once this many of its weeks are in the range.
export const PEAK_MONTH_MIN_WEEKS = 2;
// Seasonality needs this much model history (about two years).
export const SEASONALITY_MIN_HISTORY_WEEKS = 104;
// A stretch reads quieter / busier than an average month beyond these multiples.
export const QUIETER_MAX_INDEX = 0.92;
export const BUSIER_MIN_INDEX = 1.08;
// No more than this many channel cards share the same insight chip.
export const MAX_CARDS_PER_CHIP = 2;
export const TOTAL_HIGHLIGHT_CARDS = 6;
// At most this many portfolio cards lead; channel cards fill the rest.
export const MAX_PORTFOLIO_CARDS = 3;
// The year-over-year card reads "on about the same spend" while weekly spend moved less than this.
export const SAME_SPEND_MAX_CHANGE = 0.05;
// The spike card needs this many of the 52 weeks ending at the range end to judge a normal week.
export const SPIKE_MIN_HISTORY_WEEKS = 13;

// ---- Headline tiles ----
// Below this many weeks the charts plot weeks; at or above, months. Mirrors the Executive Summary.
export const WEEKLY_CUTOFF_WEEKS = 13;
// The return tile reads "<N>-week average" under this many weekly rows, "<N>-month average" after.
export const MONTH_CONTEXT_MIN_WEEKS = 9;
// A month at either edge of the range is left off the charts (not the totals) when it holds
// fewer weeks than this, so a one-week stub doesn't read as a real month.
export const MIN_EDGE_MONTH_WEEKS = 2;
// Months whose return sits at least this multiple of the range's average return are its strong
// stretch (revenue KPIs); for cost-per-conversion KPIs, months at or under the inverse are its
// cheapest stretch.
export const STRONG_RETURN_MIN_MULTIPLE = 1.15;
// A strong stretch longer than this many months stops reading as a standout (a weak holiday
// season can lift most of the year over the bar), so the chip names the best month instead.
export const MAX_STRONG_STRETCH_MONTHS = 3;
// A month (or week) is a spend or results peak at this multiple of the range's average month (or week).
export const PEAK_MIN_MULTIPLE = 1.5;
// Rough upper bound on a tile tooltip's width. Within this many pixels of the tiles' right edge,
// the tooltip flips to the cursor's left so it isn't cut off. The cross-channel spark charts use
// the same width against the viewport's edge.
export const TOOLTIP_FLIP_EDGE_PX = 300;
